import React, { useEffect, useState } from "react";
import "./StandardTemplatesList.scss";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { CommonEnums } from "../../../enums/common.enums";
import { TemplatesApi } from "../../../utils/api/Templates";
import Loading from "../../loading/Loading";
import VidioIcon from "../../../components/icons/VidioIcon";
import TemplateButtons from "./TemplateButtons";
import LeftArrowIcon from "../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../components/icons/RightArrowIcon";
import { getImageUrl } from "../../../utils/helpers/common";
import RoundTrueIcon from "../../../components/icons/RoundTrueIcon";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

interface StateType {
  isLoading: boolean;
  listData: any[];
  totalPage: number;
  pageNumber: number;
}

interface Props {
  selectedId?: string;
  subscriptionTypeProp?: string;
  listingTypeProp?: string;
  postTypeProp?: string;
  templateTypeIdProp?: string;
  hideSelectedTemplate?: boolean;
  onSelectTemplate?: (id: string, imageUrl: string) => void;
}

const AgencyPersonalizedTemplatesList: React.FC<Props> = ({
  selectedId = "",
  subscriptionTypeProp,
  listingTypeProp,
  postTypeProp,
  templateTypeIdProp,
  hideSelectedTemplate,
  onSelectTemplate,
}) => {
  const [state, setState] = useState<StateType>({
    isLoading: false,
    listData: [],
    totalPage: 1,
    pageNumber: 1,
  });

  const [searchParams] = useSearchParams();
  const subscriptionType =
    subscriptionTypeProp || searchParams.get("subscription_type") || "";
  const listingType = listingTypeProp || searchParams.get("listing_type") || "";
  const postType = postTypeProp || searchParams.get("post_type") || "";
  const reduxSelectedTemplateId = useSelector(
    (state: RootState) => state.templates.selectedTemplateId
  );
  const finalSelectedId = selectedId ?? reduxSelectedTemplateId;
  const templateTypeId =
  templateTypeIdProp ||
  searchParams.get("template_type") ||
  "";

 const getImageClass = (type: string) => {
    switch (type) {
      case CommonEnums?.postType?.image_post?.key:
        return "image-post-template";
      case CommonEnums?.postType?.video_post?.key:
        return "video-post-template";
      case CommonEnums?.postType?.story_post?.key:
        return "story-post-template";
      case CommonEnums?.postType?.reel_post?.key:
        return "reel-post-template";
      default:
        return "template-image";
    }
  };

  const isVideoType = (type: string) =>
    [
      CommonEnums?.postType?.video_post?.key,
      CommonEnums?.postType?.reel_post?.key,
    ].includes(type);

  const onClickPrev = () =>
    setState((prev) => ({
      ...prev,
      pageNumber: Math.max(1, prev.pageNumber - 1),
    }));

  const onClickNext = () =>
    setState((prev) => ({
      ...prev,
      pageNumber: Math.min(prev.totalPage, prev.pageNumber + 1),
    }));

  const getListApi = async (page: number) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const res = await TemplatesApi.getAgencyPersonalizedTemplates({
        page,
        template_type_id: templateTypeId,
        post_type: postType,
        listing_type: listingType,
        subscription_type: subscriptionType,
      });

      if (res?.data?.success) {
        const filteredDocs = hideSelectedTemplate
          ? res.data.data.docs.filter(
              (template: any) => template._id !== finalSelectedId
            )
          : res.data.data.docs;

        const updatedList = filteredDocs.map((template: any) => ({
          ...template,
          selected:
            state.listData.some(
              (item) => item._id === template._id && item.selected
            ) || template._id === finalSelectedId,
          imageClass: getImageClass(template.post_type),
          isVideo: isVideoType(template.post_type),
        }));

        setState((prev) => ({
          ...prev,
          isLoading: false,
          listData: updatedList,
          totalPage: res?.data?.data?.totalPages || 1,
        }));
      } else {
        throw new Error(res?.data?.message || "Failed to load templates");
      }
    } catch (error: any) {
      setState((prev) => ({ ...prev, isLoading: false }));
      toast.error(error.message || "Something went wrong!");
    }
  };

  useEffect(() => {
    getListApi(state.pageNumber);
  }, [
    state.pageNumber,
    finalSelectedId,
    templateTypeId,
    postType,
    listingType,
    subscriptionType,
  ]);

  const handleTemplateSelect = (
    selectedTemplateId: string,
    imageUrl?: string
  ) => {
    const updatedList = state.listData.map((item) => ({
      ...item,
      selected: item._id === selectedTemplateId,
    }));

    setState((prev) => ({ ...prev, listData: updatedList }));

    if (onSelectTemplate) {
      onSelectTemplate(selectedTemplateId, imageUrl || "");
    }
  };

  return (
    <div className="standard-templates-list-container">
      {state.isLoading ? (
        <div className="list-page-loading">
          <Loading />
        </div>
      ) : state.listData.length === 0 ? (
        <div className="data-not-found">
          <p>Templates not found!</p>
        </div>
      ) : (
        <div className="select-template-container">
          {state.listData.map((data: any) => (
            <div className="select-template-box" key={data._id}>
              <div className={`template-image-common ${data.imageClass}`}>
                <img
                  src={getImageUrl(
                    data?.template_image_ref?.compressed_file_path
                  )}
                  alt={data?.name || "template"}
                />
                {data.isVideo && (
                  <div className="vidio-icon-bg">
                    <div className="vidio-icon">
                      <VidioIcon />
                    </div>
                  </div>
                )}
                {data.selected && (
                  <div className="selected-pill">
                    <RoundTrueIcon />
                    <span>Selected</span>
                  </div>
                )}
              </div>
              <div className="template-box-text-contain">
                <p className="template-box-text-title">{data.name}</p>
                <TemplateButtons
                  templateData={data}
                  onTemplateSelect={() =>
                    handleTemplateSelect(
                      data._id,
                      getImageUrl(
                        data?.template_image_ref?.compressed_file_path
                      )
                    )
                  }
                  templateTypeId={templateTypeId}
                  postType={postType}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {state.totalPage > 1 && (
        <div className="template-list-pagination">
          <button
            className="preview-box pagination-button"
            onClick={onClickPrev}
            disabled={state.pageNumber === 1}
          >
            <LeftArrowIcon />
          </button>
          <div className="pagination-page-no">
            <p>
              Page {state.pageNumber} of {state.totalPage}
            </p>
          </div>
          <button
            className="next-box pagination-button"
            onClick={onClickNext}
            disabled={state.pageNumber === state.totalPage}
          >
            <RightArrowIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default AgencyPersonalizedTemplatesList;
